import { Card, Tooltip } from 'antd'
import { ArrowDownRight, ArrowUpRight, Minus, type LucideIcon } from 'lucide-react'
import { cn } from '@/utils/index.ts'
import { formatMoney, growth } from './utils/format.ts'

type MetricProps = {
  title: string
  value: string | number
  money?: boolean
  previous?: string | number
  year?: string | number
  description?: string
}

function MetricValue({ value, money }: Pick<MetricProps, 'value' | 'money'>) {
  return money ? (
    <>
      <span className="text-content-muted mr-1.5 text-base font-normal">¥</span>
      {formatMoney(String(value))}
    </>
  ) : (
    Number(value).toLocaleString('zh-CN')
  )
}

function MetricComparison({ value, previous, year }: Pick<MetricProps, 'value' | 'previous' | 'year'>) {
  if (previous === undefined) {
    return null
  }
  return (
    <div className="text-content-muted flex flex-wrap gap-x-4 gap-y-2 text-xs">
      {[
        { label: '环比', base: previous },
        { label: '同比', base: year }
      ].map(({ label, base }) => {
        if (base === undefined) {
          return null
        }
        const change = growth(value, base)
        let Icon = null
        if (change.startsWith('+')) {
          Icon = ArrowUpRight
        } else if (change.startsWith('-')) {
          Icon = ArrowDownRight
        } else if (change === '持平' || change === '0.0%') {
          Icon = Minus
        }
        let comparisonDescription = '增长率 =（本期 − 对比期）÷ 对比期 × 100%。'
        if (Number(base) === 0) {
          comparisonDescription = '对比值为 0，不能作为除数计算增长率；两期都为 0 时显示持平。'
        } else if (Number(base) < 0) {
          comparisonDescription = '对比值为负数，不展示百分比，避免产生误导。'
        }
        return (
          <Tooltip
            key={label}
            trigger={['hover', 'focus', 'click']}
            title={`本期：${value}；对比期：${base}。${comparisonDescription}`}
          >
            <button type="button" className="inline-flex cursor-help items-center gap-1">
              {label}
              {Icon && <Icon className="size-3.5 shrink-0" aria-hidden />}
              <span className="tabular-nums">{change}</span>
            </button>
          </Tooltip>
        )
      })}
    </div>
  )
}

export function MetricCard({
  title,
  value,
  money = false,
  previous,
  year,
  description,
  icon: Icon,
  emphasis
}: MetricProps & {
  icon?: LucideIcon
  emphasis?: 'accent'
}) {
  return (
    <Card
      className={cn('flex h-full flex-col overflow-hidden rounded-2xl!', emphasis === 'accent' && 'border-accent/25!')}
      classNames={{ body: cn('flex-1 p-5 sm:p-6', emphasis === 'accent' && 'bg-accent/5') }}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-content-muted text-sm font-medium">{title}</span>
        {Icon && (
          <span
            className={cn(
              'bg-surface-muted text-content-muted flex size-10 shrink-0 items-center justify-center rounded-xl',
              emphasis === 'accent' && 'bg-accent/10 text-accent'
            )}
          >
            <Icon className="size-5" aria-hidden />
          </span>
        )}
      </div>
      <div
        className={cn(
          'text-content my-4 text-3xl leading-tight font-semibold tracking-tight break-words tabular-nums sm:text-4xl',
          money && 'sm:text-3xl 2xl:text-4xl'
        )}
      >
        <MetricValue value={value} money={money} />
      </div>
      {(previous !== undefined || description) && (
        <div className="border-border/80 border-t pt-3">
          <MetricComparison value={value} previous={previous} year={year} />
          {description && <p className="text-content-muted m-0 text-xs leading-relaxed">{description}</p>}
        </div>
      )}
    </Card>
  )
}

export function MetricStat({ title, value, money, previous, year, description }: MetricProps) {
  return (
    <div className="min-w-0">
      <div className="text-content-muted text-xs font-medium">{title}</div>
      <div className="text-content my-2 text-2xl font-semibold tracking-tight break-words tabular-nums">
        <MetricValue value={value} money={money} />
      </div>
      <MetricComparison value={value} previous={previous} year={year} />
      {description && <p className="text-content-muted m-0 text-xs leading-relaxed">{description}</p>}
    </div>
  )
}
